import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Building2, Save, Loader2, Palette, Globe, Users, Settings, Plus, Trash2, Upload,
  Image as ImageIcon, KeyRound, ShieldCheck, Mail, UserCog, Copy, Check, CheckCircle2,
  XCircle, Clock, RefreshCw, UserCheck, Briefcase, Calendar, FileText, BarChart3,
  MessageSquare, Info, Sparkles, Brain, Cpu, Sliders, RotateCcw, Video, DollarSign,
  AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { useModalLayer } from '../../lib/modalStack';
import DoubleConfirmationModal from './DoubleConfirmationModal';
import { useWorkspaces } from './WorkspaceProvider';
import CustomRolePermissionsEditor, { createDefaultNoAccessPermissions } from '../admin/CustomRolePermissionsEditor';
import LegalSettingsSection from '../legal/LegalSettingsSection';
import { auth } from '../../lib/firebase';
import { workspaceSubdomainHost, getRootDomain } from '../../lib/platformDomain';

interface WsSettings {
  workspaceId: string;
  slug: string;
  /** The workspace's permanent original slug (never changes) and every
   * slug it has previously used as primary — all keep resolving. */
  originalSlug?: string;
  slugAliases?: string[];
  orgName: string;
  name: string;
  plan: string;
  status: string;
  country?: string;
  timezone?: string;
  defaultLanguage?: string;
  customDomains?: string[];
  signupRoles?: string[];
  emailSender?: { name?: string; email?: string };
  branding?: {
    primary?: string; primaryLight?: string; primaryDark?: string; accent?: string;
    textLight?: string; logoUrl?: string; faviconUrl?: string; sidebarLogoUrl?: string; emailLogoUrl?: string; loginLogoUrl?: string; invoiceLogoUrl?: string; businessName?: string;
  };
  /** UID of the workspace master / primary admin (not removable). */
  primaryAdminUid?: string;
  createdAt?: string;
}

interface WsMember {
  uid: string;
  email: string;
  name?: string;
  role: string;
  profilePhotoUrl?: string;
  hasPassword?: boolean;
  invitePending?: boolean;
  inviteExpiresAt?: string | null;
  inviteSentAt?: string | null;
  bookingAccessEnabled?: boolean;
}

type Tab = 'overview' | 'branding' | 'domain' | 'agents' | 'screening-staff' | 'roles' | 'signup' | 'email' | 'llm' | 'video' | 'stripe' | 'legal';
type Asset = 'logo' | 'favicon' | 'sidebar-logo' | 'email-logo' | 'login-logo' | 'invoice-logo';

interface WorkspaceRole {
  roleId: string;
  workspaceId: string;
  name: string;
  permissions: Record<string, string>; // module → 'none' | 'view' | 'edit'
  archived?: boolean;
}

interface EmailDomain {
  id: string;
  domain: string;
  domainKey?: string;
  bounceSubdomain?: string;
  status?: string;
  dnsRecords?: { dkim?: any; cname?: any };
  mailagentKeys?: string[];
  providerConfigured?: boolean;
  createdAt?: string;
}

interface MailAgent { key: string; name: string; active?: boolean; }

interface WebDomain {
  id: string;
  domain: string;
  token: string;
  verificationHost: string;
  status?: string; // 'pending' | 'verified' | 'lost'
  createdAt?: string;
  verifiedAt?: string;
  lastCheckedAt?: string;
}

const MAX_CUSTOM_ROLES = 5;

const ROLE_MODULES: { key: string; label: string; icon: React.ComponentType<any>; desc: string }[] = [
  { key: 'applicants', label: 'Applicants', icon: UserCheck, desc: 'Applicant profiles & documents' },
  { key: 'finance', label: 'Finance', icon: DollarSign, desc: 'Invoices, payments & products' },
  { key: 'jobs', label: 'Jobs', icon: Briefcase, desc: 'Job postings & hiring' },
  { key: 'employers', label: 'Employers', icon: Building2, desc: 'Employer accounts & host partners' },
  { key: 'referrers', label: 'Referrers', icon: Users, desc: 'Agencies & referral partners' },
  { key: 'sponsors', label: 'Sponsors', icon: ShieldCheck, desc: 'Sponsorship management' },
  { key: 'organizations', label: 'Organizations', icon: Building2, desc: 'Partner organizations' },
  { key: 'batch', label: 'Batch Center', icon: Sliders, desc: 'Cohorts & bulk requests' },
  { key: 'events', label: 'Events', icon: Calendar, desc: 'Meetings, schedules & calendars' },
  { key: 'reports', label: 'Reports', icon: BarChart3, desc: 'Analytics, exports & stats' },
  { key: 'approvals', label: 'Approvals', icon: ShieldCheck, desc: 'Pending approvals queue' },
  { key: 'settings', label: 'Settings', icon: Settings, desc: 'Workspace configuration' },
  { key: 'templates', label: 'Email Templates', icon: Mail, desc: 'Broadcasts & email templates' },
  { key: 'doc-templates', label: 'Doc Templates', icon: FileText, desc: 'Document templates & contracts' },
  { key: 'users', label: 'Users', icon: Users, desc: 'User accounts & workspace team members' },
  { key: 'tickets', label: 'Support Tickets', icon: MessageSquare, desc: 'Inbound support tickets' },
  { key: 'ai-suggestions', label: 'AI Suggestions', icon: Sparkles, desc: 'AI copilot reviews & suggestions' },
  { key: 'invitations', label: 'Invitations', icon: Mail, desc: 'Candidate & team invitations' },
];

const PLANS = ['free', 'starter', 'growth', 'scale', 'enterprise'];
const TIMEZONES = ['UTC', 'America/New_York', 'Europe/London', 'Africa/Nairobi', 'Asia/Tokyo', 'Australia/Sydney'];
const SIGNUP_ROLE_OPTIONS = [
  { key: 'applicant', label: 'Applicants', desc: 'Job seekers apply & manage applications' },
  { key: 'employer', label: 'Employers / Hosts', desc: 'Companies post jobs & manage hires' },
  { key: 'referrer', label: 'Referrers / Agents', desc: 'Recruiters & agency partners' },
  { key: 'sponsor', label: 'Sponsors', desc: 'Sponsorship / SEVIS organizations' },
];

const COLOR_FIELDS: { field: keyof NonNullable<WsSettings['branding']>; label: string }[] = [
  { field: 'primary', label: 'Primary' },
  { field: 'primaryLight', label: 'Primary Light' },
  { field: 'primaryDark', label: 'Primary Dark' },
  { field: 'accent', label: 'Accent' },
  { field: 'textLight', label: 'Text on Dark' },
];


const DEFAULT_PERMS = (): Record<string, string> => ({
  ...createDefaultNoAccessPermissions(),
});

export default function WorkspaceSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const z = useModalLayer(open, onClose);
  const { refresh: refreshWorkspaces } = useWorkspaces();
  const currentUid = auth.currentUser?.uid || '';

  const [ws, setWs] = useState<WsSettings | null>(null);
  const [members, setMembers] = useState<WsMember[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [copiedLinkKey, setCopiedLinkKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [orgName, setOrgName] = useState('');
  const [country, setCountry] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [language, setLanguage] = useState('en');
  const [slug, setSlug] = useState('');
  const [branding, setBranding] = useState<NonNullable<WsSettings['branding']>>({});
  const [domains, setDomains] = useState<string[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [addingMember, setAddingMember] = useState(false);
  // Pending workspace-team invitations (invite-first Team tab) + the inline
  // outcome of the most recent invite action. Every Add / Resend / Cancel
  // ends in exactly one visible result here — never a transient alert() and
  // never a silent no-op.
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [addOutcome, setAddOutcome] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [invitationBusyId, setInvitationBusyId] = useState<string | null>(null);
  const [doubleConfirmOpen, setDoubleConfirmOpen] = useState(false);
  const [targetUidForChange, setTargetUidForChange] = useState('');
  const [newRoleForChange, setTargetNewRoleForChange] = useState('');
  const [uploadingAsset, setUploadingAsset] = useState<Asset | null>(null);
  const [signupRoles, setSignupRoles] = useState<string[]>([]);
  const [emailSenderName, setEmailSenderName] = useState('');
  const [emailSenderEmail, setEmailSenderEmail] = useState('');
  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [roleName, setRoleName] = useState('');
  const [rolePerms, setRolePerms] = useState<Record<string, string>>(DEFAULT_PERMS);
  const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({});

  const toggleRoleExpand = (roleId: string) => {
    setExpandedRoles(prev => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  // Email domains (ZeptoMail verification)
  const [emailDomains, setEmailDomains] = useState<EmailDomain[]>([]);
  const [agents, setAgents] = useState<MailAgent[]>([]);
  const [providerConfigured, setProviderConfigured] = useState(false);
  const [newSendingDomain, setNewSendingDomain] = useState('');
  const [newBouncePrefix, setNewBouncePrefix] = useState('bounce');
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState<string | null>(null);

  // Web custom domains (DNS-TXT ownership verification)
  const [webDomains, setWebDomains] = useState<WebDomain[]>([]);
  const [newWebDomain, setNewWebDomain] = useState('');
  const [claimingWebDomain, setClaimingWebDomain] = useState(false);
  const [verifyingWebDomain, setVerifyingWebDomain] = useState<string | null>(null);
  const [attachingWebDomain, setAttachingWebDomain] = useState<string | null>(null);
  const [webDomainError, setWebDomainError] = useState<string | null>(null);

  // Workspace LLM configurations
  const [llmProvider, setLlmProvider] = useState('NVIDIA');
  const [llmApiKey, setLlmApiKey] = useState('');
  const [llmModelId, setLlmModelId] = useState('');
  const [llmTab, setLlmTab] = useState<'basic' | 'advanced'>('basic');

  // Advanced configurations
  const [reasoningEffort, setReasoningEffort] = useState('medium');
  const [temperature, setTemperature] = useState('1');
  const [topP, setTopP] = useState('1');
  const [frequencyPenalty, setFrequencyPenalty] = useState('0');
  const [presencePenalty, setPresencePenalty] = useState('0');
  const [maxTokens, setMaxTokens] = useState('4096');

  const [testingLlm, setTestingLlm] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Daily.co video settings
  const [jitsiEnabled, setJitsiEnabled] = useState(true);
  const [dailyEnabled, setDailyEnabled] = useState(false);
  const [dailyApiKey, setDailyApiKey] = useState('');
  const [dailyDomain, setDailyDomain] = useState('');
  const [dailyDefaultPrivacy, setDailyDefaultPrivacy] = useState<'private' | 'public'>('private');
  const [dailyStartVideoOff, setDailyStartVideoOff] = useState(true);
  const [dailyStartAudioOff, setDailyStartAudioOff] = useState(true);
  const [dailyEnableChat, setDailyEnableChat] = useState(true);
  const [dailyEnableScreenShare, setDailyEnableScreenShare] = useState(true);
  const [dailyEnableRecording, setDailyEnableRecording] = useState<'cloud' | 'local' | 'off'>('off');
  const [defaultVideoProvider, setDefaultVideoProvider] = useState<'jitsi' | 'daily'>('jitsi');
  const [savingDaily, setSavingDaily] = useState(false);
  const [dailySaveSuccess, setDailySaveSuccess] = useState(false);
  const [dailyError, setDailyError] = useState<string | null>(null);

  // Team directory + pending invitations come from the team endpoint. Both
  // it and the settings endpoint are internal-staff only: external org
  // accounts (referrer/employer/sponsor) never mount this modal — their
  // own team management lives in their dashboards (e.g. the referrer Team
  // view over /api/referrer/agents). The separate loadTeam call stays so
  // the Team tab never renders an empty shell that looks like "no members"
  // when only the settings load fails.
  const loadTeam = async () => {
    try {
      const data = await safeFetch('/api/workspaces/team');
      if (Array.isArray(data.members)) setMembers(data.members);
      setPendingInvitations(Array.isArray(data.invitations) ? data.invitations : []);
    } catch { /* settings load below remains the fallback source for members */ }
  };

  const load = async () => {
    setLoading(true);
    await loadTeam();
    try {
      const data = await safeFetch('/api/workspaces/settings');
      const w = data.workspace;
      setWs(w);
      setOrgName(w.orgName || '');
      setCountry(w.country || '');
      setTimezone(w.timezone || 'UTC');
      setLanguage(w.defaultLanguage || 'en');
      setSlug(w.slug || '');
      setBranding(w.branding || {});
      setDomains(w.customDomains || []);
      setSignupRoles(w.signupRoles || []);
      // Default sender: <workspace name> <noreply@edutrouve.org> — used for
      // workspaces created before the default-sender migration.
      setEmailSenderName(w.emailSender?.name || w.name || '');
      setEmailSenderEmail(w.emailSender?.email || 'noreply@edutrouve.org');
      setMembers(data.members || []);
      try {
        const roleData = await safeFetch('/api/workspaces/roles');
        setRoles(roleData.roles || []);
      } catch { /* roles are optional */ }
      try {
        const domData = await safeFetch('/api/workspaces/email-domains');
        setEmailDomains(domData.domains || []);
        setProviderConfigured(!!domData.configured);
      } catch { /* optional */ }
      try {
        const webData = await safeFetch('/api/workspaces/web-domains');
        setWebDomains(webData.domains || []);
      } catch { /* optional */ }
      try {
        const agentData = await safeFetch('/api/workspaces/email-domains/agents');
        setAgents(agentData.agents || []);
        if (agentData.configured === false) setProviderConfigured(false);
      } catch { /* optional */ }

      // Fetch Workspace LLM settings
      try {
        const llmData = await safeFetch('/api/workspaces/settings/llm');
        if (llmData && llmData.llmConfig) {
          const cfg = llmData.llmConfig;
          setLlmProvider(cfg.provider || 'NVIDIA');
          setLlmApiKey(cfg.apiKey || '');
          setLlmModelId(cfg.modelId || '');
          setReasoningEffort(cfg.reasoningEffort || 'medium');
          setTemperature(cfg.temperature !== undefined ? String(cfg.temperature) : '1');
          setTopP(cfg.topP !== undefined ? String(cfg.topP) : '1');
          setFrequencyPenalty(cfg.frequencyPenalty !== undefined ? String(cfg.frequencyPenalty) : '0');
          setPresencePenalty(cfg.presencePenalty !== undefined ? String(cfg.presencePenalty) : '0');
          setMaxTokens(cfg.maxTokens !== undefined ? String(cfg.maxTokens) : '4096');
        } else {
          setLlmProvider('NVIDIA');
          setLlmApiKey('');
          setLlmModelId('');
          setReasoningEffort('medium');
          setTemperature('1');
          setTopP('1');
          setFrequencyPenalty('0');
          setPresencePenalty('0');
          setMaxTokens('4096');
        }
      } catch { /* optional */ }

      const dailyData = await safeFetch('/api/workspaces/settings/daily').catch(() => null);
      if (dailyData) {
        const dSettings = dailyData.dailySettings || {};
        setJitsiEnabled(dSettings.jitsiEnabled !== false);
        setDailyEnabled(Boolean(dSettings.dailyEnabled));
        setDailyApiKey(dSettings.apiKey || '');
        setDailyDomain(dSettings.domain || '');
        setDailyDefaultPrivacy(dSettings.defaultPrivacy || 'private');
        setDailyStartVideoOff(dSettings.startVideoOff !== false);
        setDailyStartAudioOff(dSettings.startAudioOff !== false);
        setDailyEnableChat(dSettings.enableChat !== false);
        setDailyEnableScreenShare(dSettings.enableScreenShare !== false);
        setDailyEnableRecording(dSettings.enableRecording || 'off');
        setDefaultVideoProvider(dailyData.defaultVideoProvider || 'jitsi');
      }
    } catch (e: any) {
      notifyToast('error', 'Failed to load workspace settings: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveDailySettings = async () => {
    setSavingDaily(true);
    setDailyError(null);
    setDailySaveSuccess(false);
    try {
      const payload = {
        jitsiEnabled,
        dailyEnabled,
        apiKey: dailyApiKey,
        domain: dailyDomain,
        defaultPrivacy: dailyDefaultPrivacy,
        startVideoOff: dailyStartVideoOff,
        startAudioOff: dailyStartAudioOff,
        enableChat: dailyEnableChat,
        enableScreenShare: dailyEnableScreenShare,
        enableRecording: dailyEnableRecording,
        defaultVideoProvider,
      };
      const res = await safeFetch('/api/workspaces/settings/daily', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.dailySettings) {
        setDailyApiKey(res.dailySettings.apiKey || '');
      }
      setDailySaveSuccess(true);
      setTimeout(() => setDailySaveSuccess(false), 3000);
    } catch (err: any) {
      setDailyError(err.message || 'Failed to save Daily video settings.');
    } finally {
      setSavingDaily(false);
    }
  };

  const saveAndTestLlm = async () => {
    setTestingLlm(true);
    setTestResult(null);
    try {
      const data = await safeFetch('/api/workspaces/settings/llm/save-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: llmProvider,
          apiKey: llmApiKey.trim(),
          modelId: llmModelId.trim(),
          reasoningEffort,
          temperature: temperature !== '' ? Number(temperature) : undefined,
          topP: topP !== '' ? Number(topP) : undefined,
          frequencyPenalty: frequencyPenalty !== '' ? Number(frequencyPenalty) : undefined,
          presencePenalty: presencePenalty !== '' ? Number(presencePenalty) : undefined,
          maxTokens: maxTokens !== '' ? Number(maxTokens) : undefined,
        })
      });
      setTestResult({ success: true, message: data.message || 'Configuration tested and saved successfully!' });
      await load();
    } catch (e: any) {
      setTestResult({ success: false, message: e.message || 'Test connection failed.' });
    } finally {
      setTestingLlm(false);
    }
  };

  const resetLlmToDefaults = () => {
    setReasoningEffort('medium');
    setTemperature('1');
    setTopP('1');
    setFrequencyPenalty('0');
    setPresencePenalty('0');
    setMaxTokens('4096');
    setTestResult(null);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const saveSettings = async () => {
    const prevSlug = (ws?.slug || '').trim().toLowerCase();
    const nextSlug = slug.trim().toLowerCase();
    setSaving(true);
    setSaved(false);
    try {
      await safeFetch('/api/workspaces/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // customDomains is intentionally NOT sent here: attached domains
          // change only through the verified claim → verify → attach flow
          // (Domains tab), which PATCHes the field immediately. Sending the
          // local list with every save could race or resurrect domains.
          orgName, branding, country, timezone, defaultLanguage: language,
          signupRoles, emailSender: { name: emailSenderName, email: emailSenderEmail },
          ...(nextSlug && nextSlug !== prevSlug ? { slug: nextSlug } : {}),
        }),
      });
      await saveDailySettings().catch(() => {});
      setSaved(true);
      refreshWorkspaces();
      setTimeout(() => setSaved(false), 2500);
      // Slug changes are ADDITIVE: setting a new primary subdomain keeps
      // the original — and every previous primary — resolving forever, so
      // there is deliberately NO redirect off the old host here. The old
      // subdomain the admin may be sitting on keeps working by design.
    } catch (e: any) {
      notifyToast('error', 'Failed to save settings: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSignupRole = (key: string) => {
    setSignupRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };

  const uploadBranding = async (type: Asset, file: File) => {
    setUploadingAsset(type);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const data = await safeFetch('/api/workspaces/branding/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, fileBase64: base64, contentType: file.type || 'image/png' }),
      });
      const key = type === 'logo' ? 'logoUrl' : type === 'favicon' ? 'faviconUrl' : type === 'sidebar-logo' ? 'sidebarLogoUrl' : type === 'email-logo' ? 'emailLogoUrl' : type === 'invoice-logo' ? 'invoiceLogoUrl' : 'loginLogoUrl';
      setBranding(b => ({ ...b, [key]: data.branding?.[key] }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      notifyToast('error', 'Upload failed: ' + e.message);
    } finally {
      setUploadingAsset(null);
    }
  };

  // Invite-first: Add creates a pending workspace-team invitation (it never
  // stamps membership or creates an account on the spot). EVERY outcome —
  // invitation created, email-delivery warning, or a specific refusal from
  // the server — is rendered inline in the Team tab.
  const addMember = async () => {
    const trimmedEmail = newMemberEmail.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setAddOutcome({ kind: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    const chosenRole = newMemberRole;
    setAddingMember(true);
    setAddOutcome(null);
    try {
      const data = await safeFetch('/api/workspaces/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, role: chosenRole }),
      });
      setNewMemberEmail('');
      setNewMemberRole('member');
      await loadTeam();
      if (data?.emailSent === false) {
        setAddOutcome({
          kind: 'success',
          message: `Invitation created for ${trimmedEmail}, but the invite email could not be delivered. They can still accept it after signing in here, or resend it from the pending list below.`,
        });
      } else {
        setAddOutcome({
          kind: 'success',
          message: `Invitation sent to ${trimmedEmail}. They'll join this team as ${roleDisplayLabel(chosenRole)} as soon as they accept — the invitation stays in the pending list below until then.`,
        });
      }
    } catch (e: any) {
      setAddOutcome({ kind: 'error', message: (e?.message || 'Failed to invite team member.').toString() });
    } finally {
      setAddingMember(false);
    }
  };

  const resendTeamInvitation = async (invitationId: string, email?: string) => {
    if (!(await confirmDialog(`Resend the invitation email${email ? ` to ${email}` : ''} with a fresh invitation link?`, { confirmLabel: 'Resend invitation' }))) return;
    setInvitationBusyId(invitationId);
    setAddOutcome(null);
    try {
      await safeFetch(`/api/workspaces/team-invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      await loadTeam();
      setAddOutcome({ kind: 'success', message: 'Invitation email resent with a fresh link.' });
    } catch (e: any) {
      setAddOutcome({ kind: 'error', message: (e?.message || 'Failed to resend the invitation.').toString() });
    } finally {
      setInvitationBusyId(null);
    }
  };

  const cancelTeamInvitation = async (invitationId: string, email: string) => {
    if (!(await confirmDialog(`Cancel the pending invitation for ${email}?`))) return;
    setInvitationBusyId(invitationId);
    setAddOutcome(null);
    try {
      await safeFetch(`/api/workspaces/team-invitations/${invitationId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      await loadTeam();
      setAddOutcome({ kind: 'success', message: `Invitation for ${email} cancelled.` });
    } catch (e: any) {
      setAddOutcome({ kind: 'error', message: (e?.message || 'Failed to cancel the invitation.').toString() });
    } finally {
      setInvitationBusyId(null);
    }
  };

  const resendInvite = async (uid: string) => {
    if (!(await confirmDialog('Resend the welcome email with a fresh password setup link to this member?'))) return;
    try {
      await safeFetch(`/api/workspaces/members/${uid}/resend-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      await load();
      notifyToast('success', 'Invite email resent successfully. The new link expires in 72 hours.');
    } catch (e: any) {
      notifyToast('error', 'Failed to resend invite: ' + e.message);
    }
  };

  const removeMember = async (uid: string) => {
    const memberLabel = members.find(m => m.uid === uid)?.name || members.find(m => m.uid === uid)?.email || 'this member';
    if (!(await confirmDialog(`Remove ${memberLabel} from the workspace? They will lose access to this workspace immediately.`))) return;
    // Drop them from the list immediately so the UI never lingers on a removed
    // member; the server round-trip reconciles the real list in the background.
    setMembers(prev => prev.filter(m => m.uid !== uid));
    try {
      await safeFetch(`/api/workspaces/members/${uid}`, { method: 'DELETE' });
      await load();
    } catch (e: any) {
      notifyToast('error', 'Failed to remove member: ' + e.message);
      await load();
      return;
    }
    notifyToast('success', 'Member removed successfully.');
  };

  const changeRole = (uid: string, role: string) => {
    setTargetUidForChange(uid);
    setTargetNewRoleForChange(role);
    setDoubleConfirmOpen(true);
  };

  const createRole = async () => {
    if (!roleName.trim()) { notifyToast('warning', 'Role name is required.'); return; }
    try {
      await safeFetch('/api/workspaces/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roleName.trim(), permissions: rolePerms }),
      });
      setRoleName('');
      setRolePerms(DEFAULT_PERMS());
      await load();
    } catch (e: any) {
      notifyToast('error', 'Failed to create role: ' + e.message);
    }
  };

  const updateRoleFullPermissions = async (roleId: string, updatedPerms: Record<string, string>) => {
    setRoles(prev => prev.map(r => r.roleId === roleId ? { ...r, permissions: updatedPerms } : r));
    try {
      await safeFetch(`/api/workspaces/roles/${roleId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: updatedPerms }),
      });
    } catch (e: any) {
      notifyToast('error', 'Failed to update role permissions: ' + e.message);
      await load();
    }
  };

  const archiveRole = async (roleId: string) => {
    const role = roles.find(r => r.roleId === roleId);
    if (!role) return;
    const usersOfRole = classifiedMembers.filter(c => (c.m.role || '') === role.name).length;
    const suffix = usersOfRole > 0
      ? `\n\n${usersOfRole} member${usersOfRole === 1 ? '' : 's'} currently hold${usersOfRole === 1 ? 's' : ''} this role and will lose its access immediately. Reassign them from the Team tab.`
      : '';
    if (!(await confirmDialog(`Archive custom role "${role.name}"?${suffix}`))) return;
    try {
      await safeFetch(`/api/workspaces/roles/${roleId}`, { method: 'DELETE' });
      await load();
    } catch (e: any) {
      notifyToast('error', 'Failed to archive role: ' + e.message);
    }
  };

  // ── Email sending domains (ZeptoMail) ──────────────────────────────
  const addSendingDomain = async () => {
    const d = newSendingDomain.trim().toLowerCase();
    if (!d) { notifyToast('warning', 'Enter a domain, e.g. example.com'); return; }
    setAddingDomain(true);
    try {
      const data = await safeFetch('/api/workspaces/email-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: d, subDomainPrefix: newBouncePrefix.trim() || 'bounce' }),
      });
      if (data.domain) setEmailDomains(prev => [data.domain, ...prev]);
      setNewSendingDomain('');
      setNewBouncePrefix('bounce');
    } catch (e: any) {
      notifyToast('error', 'Failed to add sending domain: ' + e.message);
    } finally {
      setAddingDomain(false);
    }
  };

  const verifySendingDomain = async (id: string) => {
    setVerifyingDomain(id);
    try {
      const data = await safeFetch(`/api/workspaces/email-domains/${id}/verify`, { method: 'PUT' });
      setEmailDomains(prev => prev.map(d => d.id === id ? { ...d, status: data.status } : d));
    } catch (e: any) {
      notifyToast('error', 'Verification failed: ' + e.message);
    } finally {
      setVerifyingDomain(null);
    }
  };

  const deleteSendingDomain = async (id: string, domain: string) => {
    if (!(await confirmDialog(`Remove sending domain "${domain}"?`))) return;
    try {
      await safeFetch(`/api/workspaces/email-domains/${id}`, { method: 'DELETE' });
      setEmailDomains(prev => prev.filter(d => d.id !== id));
    } catch (e: any) {
      notifyToast('error', 'Failed to remove sending domain: ' + e.message);
    }
  };

  // ── Web custom domains (DNS-TXT ownership verification) ──────────
  // A domain can only be ATTACHED after this workspace has proven
  // ownership of it (claim → publish TXT → verify). All failures in
  // this flow surface in an inline banner — never alert().
  const patchAttachedDomains = async (next: string[]) => {
    await safeFetch('/api/workspaces/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customDomains: next }),
    });
    setDomains(next);
    refreshWorkspaces();
  };

  const claimWebDomain = async () => {
    const d = newWebDomain.trim().toLowerCase();
    if (!d) { setWebDomainError('Enter a domain, e.g. portal.example.com'); return; }
    setClaimingWebDomain(true);
    setWebDomainError(null);
    try {
      const data = await safeFetch('/api/workspaces/web-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: d }),
      });
      if (data.domain) setWebDomains(prev => [data.domain, ...prev]);
      setNewWebDomain('');
    } catch (e: any) {
      setWebDomainError(e.message || 'Failed to claim domain.');
    } finally {
      setClaimingWebDomain(false);
    }
  };

  const verifyWebDomainRec = async (id: string) => {
    setVerifyingWebDomain(id);
    setWebDomainError(null);
    try {
      const data = await safeFetch(`/api/workspaces/web-domains/${id}/verify`, { method: 'PUT' });
      setWebDomains(prev => prev.map(d => d.id === id ? { ...d, status: data.status } : d));
      if (!data.matched) {
        setWebDomainError('The TXT record was not found yet. DNS changes can take a while to propagate — check the host and value below match exactly, then verify again.');
      }
    } catch (e: any) {
      setWebDomainError(e.message || 'Verification failed.');
    } finally {
      setVerifyingWebDomain(null);
    }
  };

  const releaseWebDomain = async (id: string, domain: string) => {
    if (!(await confirmDialog(`Release the claim on "${domain}"? The verification record is deleted. (An attached domain stays attached until you remove it above.)`))) return;
    setWebDomainError(null);
    try {
      await safeFetch(`/api/workspaces/web-domains/${id}`, { method: 'DELETE' });
      setWebDomains(prev => prev.filter(d => d.id !== id));
    } catch (e: any) {
      setWebDomainError(e.message || 'Failed to release domain.');
    }
  };

  const attachWebDomain = async (domain: string) => {
    setAttachingWebDomain(domain);
    setWebDomainError(null);
    try {
      await patchAttachedDomains(domains.includes(domain) ? domains : [...domains, domain]);
    } catch (e: any) {
      setWebDomainError(e.message || 'Failed to attach domain.');
    } finally {
      setAttachingWebDomain(null);
    }
  };

  const detachWebDomain = async (domain: string) => {
    if (!(await confirmDialog(`Remove "${domain}" from this workspace? The portal will stop resolving on this host.`))) return;
    setAttachingWebDomain(domain);
    setWebDomainError(null);
    try {
      await patchAttachedDomains(domains.filter(x => x !== domain));
    } catch (e: any) {
      setWebDomainError(e.message || 'Failed to remove domain.');
    } finally {
      setAttachingWebDomain(null);
    }
  };

  const toggleAgentOnDomain = async (domainId: string, agentKey: string) => {
    const dom = emailDomains.find(d => d.id === domainId);
    const current = dom?.mailagentKeys || [];
    const next = current.includes(agentKey) ? current.filter(k => k !== agentKey) : [...current, agentKey];
    try {
      await safeFetch(`/api/workspaces/email-domains/${domainId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailagentKeys: next }),
      });
      setEmailDomains(prev => prev.map(d => d.id === domainId ? { ...d, mailagentKeys: next } : d));
    } catch (e: any) {
      notifyToast('error', 'Failed to update mail agents: ' + e.message);
    }
  };

  const activeRoles = roles.filter(r => !r.archived);
  // Workspace-assignable roles (admin / member / custom roles). Profile roles
  // like employer/referrer/sponsor are shown as read-only badges instead.
  // Viewer is a real, server-enforced role (read-only everywhere). Keeping it
  // out of the assignable options made "Viewer" members impossible to create and
  // inconsistent to edit.
  const roleOptions = ['admin', 'manager', 'member', 'viewer', ...activeRoles.map(r => r.name)];
  // Adding members is kept simple: Admin or Team Member (+ any custom roles).
  // Manager/Viewer remain assignable via the edit dropdown for existing members.
  const addRoleOptions = ['admin', 'member', ...activeRoles.map(r => r.name)];

  // ── Team directory classification ─────────────────────────────────────
  // The Team tab distinguishes three kinds of rows so each member's state is
  // obvious and only valid actions are offered:
  //   • team members (workspace roles incl. custom roles) — fully manageable;
  //   • privileged tiers (owner / super / master) — read-only badges;
  //   • referrer agents (workspace-linked referrer accounts) — read-only,
  //     managed from the referrer console.
  // Employers, sponsors and applicants are filtered out entirely (server-side
  // too) — they are NOT workspace team members.
  const builtInMemberRoles = ['admin', 'manager', 'member', 'team_member', 'viewer'];
  const editableWorkspaceRole = (role: string) =>
    builtInMemberRoles.includes(role) || activeRoles.some(cr => cr.name === role);
  const roleDisplayLabel = (role: string) =>
    (({ admin: 'Admin', manager: 'Manager', member: 'Team Member', team_member: 'Team Member', viewer: 'Viewer', owner: 'Owner', master: 'Owner', super: 'Super Admin' }) as Record<string, string>)[role] || role;

  type MemberBucket = 'team' | 'agent' | 'privileged' | 'hidden' | 'archived-role';
  const bucketForMember = (m: WsMember): MemberBucket => {
    const r = (m.role || '').toLowerCase();
    if (r === 'applicant' || r === 'employer' || r === 'sponsor') return 'hidden';
    // The workspace's primary admin / master is protected server-side (never
    // demotable, never removable) — surface it as a read-only owner row even
    // when its stored role is an ordinary 'admin' value.
    if (ws && m.uid === ws.primaryAdminUid) return 'privileged';
    if (r === 'referrer') return 'agent';
    if (r === 'master' || r === 'owner' || r === 'super') return 'privileged';
    if (editableWorkspaceRole(m.role)) return 'team';
    // The role name is no longer an active role here (e.g. an archived custom
    // role) — still a member, but needs a reassignment to stay manageable.
    return 'archived-role';
  };
  const classifiedMembers = members
    .map(m => ({ m, bucket: bucketForMember(m) }))
    .filter(c => c.bucket !== 'hidden');
  const teamRows = classifiedMembers.filter(c => c.bucket === 'team' || c.bucket === 'archived-role');
  const privilegedRows = classifiedMembers.filter(c => c.bucket === 'privileged');
  const agentRows = classifiedMembers.filter(c => c.bucket === 'agent');
  const pendingInviteCount = classifiedMembers.filter(c => c.m.invitePending && !c.m.hasPassword).length;
  const roleMemberCount = (name: string) => classifiedMembers.filter(c => (c.m.role || '') === name).length;

  const TABS: { key: Tab; label: string; icon: React.ComponentType<any> }[] = [
    { key: 'overview', label: 'Overview', icon: Settings },
    { key: 'branding', label: 'Branding', icon: Palette },
    { key: 'domain', label: 'Domains', icon: Globe },
    { key: 'agents', label: 'Workspace Team', icon: Users },
    { key: 'screening-staff', label: 'Screening Staff', icon: UserCheck },
    { key: 'roles', label: 'Custom Roles', icon: UserCog },
    { key: 'signup', label: 'Signup Roles', icon: ShieldCheck },
    { key: 'email', label: 'Email Sender', icon: Mail },
    { key: 'llm', label: 'Call LLM Model', icon: Sparkles },
    { key: 'video', label: 'Video Meetings', icon: Video },
    { key: 'stripe', label: 'Stripe Payments', icon: DollarSign },
    { key: 'legal', label: 'Legal', icon: FileText },
  ];

  // Screening Staff Settings State
  const [screeningStaffUids, setScreeningStaffUids] = useState<string[]>([]);
  const [autoAssignOnBooking, setAutoAssignOnBooking] = useState(false);
  const [screeningType, setScreeningType] = useState<'collective' | 'round_robin'>('collective');
  const [screeningDurationMinutes, setScreeningDurationMinutes] = useState(30);
  const [advanceBookingAmount, setAdvanceBookingAmount] = useState<number>(14);
  const [advanceBookingUnit, setAdvanceBookingUnit] = useState<'days' | 'weeks' | 'months'>('days');
  const [notifyAdminOnBooking, setNotifyAdminOnBooking] = useState(false);
  const [screeningLocationType, setScreeningLocationType] = useState<'daily' | 'jitsi' | 'google_meet' | 'zoom' | 'custom_url'>('daily');
  const [screeningLocationDetails, setScreeningLocationDetails] = useState('');
  const [eligibleScreeningMembers, setEligibleScreeningMembers] = useState<WsMember[]>([]);
  const [loadingScreeningStaff, setLoadingScreeningStaff] = useState(false);
  const [savingScreeningStaff, setSavingScreeningStaff] = useState(false);

  const fetchScreeningStaffSettings = useCallback(async () => {
    setLoadingScreeningStaff(true);
    try {
      const data = await safeFetch('/api/workspaces/settings/screening-staff');
      if (data) {
        const cfg = data.screeningStaffConfig || {};
        setScreeningStaffUids(cfg.screeningStaffUids || []);
        setAutoAssignOnBooking(Boolean(cfg.autoAssignOnBooking));
        setScreeningType(cfg.screeningType || 'collective');
        setScreeningDurationMinutes(cfg.screeningDurationMinutes || 30);
        setAdvanceBookingAmount(cfg.advanceBookingAmount || 14);
        setAdvanceBookingUnit(cfg.advanceBookingUnit || 'days');
        setNotifyAdminOnBooking(Boolean(cfg.notifyAdminOnBooking));
        setScreeningLocationType(cfg.screeningLocationType || 'daily');
        setScreeningLocationDetails(cfg.screeningLocationDetails || '');
        setEligibleScreeningMembers(data.eligibleMembers || []);
      }
    } catch (err) {
      console.error('Failed to load screening staff settings:', err);
    } finally {
      setLoadingScreeningStaff(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'screening-staff') {
      fetchScreeningStaffSettings();
    }
  }, [tab, fetchScreeningStaffSettings]);

  const saveScreeningStaffSettings = async () => {
    setSavingScreeningStaff(true);
    try {
      const data = await safeFetch('/api/workspaces/settings/screening-staff', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screeningStaffUids,
          autoAssignOnBooking,
          screeningType,
          screeningDurationMinutes,
          advanceBookingAmount,
          advanceBookingUnit,
          notifyAdminOnBooking,
          screeningLocationType,
          screeningLocationDetails,
        }),
      });
      if (data?.screeningStaffConfig) {
        setScreeningStaffUids(data.screeningStaffConfig.screeningStaffUids || []);
        setAutoAssignOnBooking(Boolean(data.screeningStaffConfig.autoAssignOnBooking));
        setScreeningType(data.screeningStaffConfig.screeningType || 'collective');
        setScreeningDurationMinutes(data.screeningStaffConfig.screeningDurationMinutes || 30);
        setAdvanceBookingAmount(data.screeningStaffConfig.advanceBookingAmount || 14);
        setAdvanceBookingUnit(data.screeningStaffConfig.advanceBookingUnit || 'days');
        setNotifyAdminOnBooking(Boolean(data.screeningStaffConfig.notifyAdminOnBooking));
        setScreeningLocationType(data.screeningStaffConfig.screeningLocationType || 'daily');
        setScreeningLocationDetails(data.screeningStaffConfig.screeningLocationDetails || '');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      notifyToast.success('Screening staff settings saved successfully');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save screening staff settings');
    } finally {
      setSavingScreeningStaff(false);
    }
  };

  const toggleScreeningStaffUid = (uid: string) => {
    setScreeningStaffUids(prev =>
      prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]
    );
  };

  // Stripe Settings State
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeStatus, setStripeStatus] = useState<'Connected' | 'Not Connected'>('Not Connected');
  const [stripeLastVerified, setStripeLastVerified] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeTesting, setStripeTesting] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchStripeSettings = useCallback(async () => {
    setStripeLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/workspaces/settings/stripe', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const settings = data.stripeSettings || {};
        setStripePublishableKey(settings.publishableKey || '');
        setStripeSecretKey(settings.secretKey || '');
        setStripeStatus(settings.status === 'Connected' ? 'Connected' : 'Not Connected');
        setStripeLastVerified(settings.lastVerifiedAt || null);
      }
    } catch (err) {
      console.error('Failed to load Stripe settings:', err);
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'stripe') {
      fetchStripeSettings();
    }
  }, [tab, fetchStripeSettings]);

  const saveStripeSettings = async () => {
    setSaving(true);
    setStripeTestResult(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/workspaces/settings/stripe', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          publishableKey: stripePublishableKey,
          secretKey: stripeSecretKey,
        }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to save Stripe settings');
      }

      const data = await res.json();
      setStripeStatus(data.stripeSettings?.status === 'Connected' ? 'Connected' : 'Not Connected');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save Stripe settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestStripeConnection = async () => {
    setStripeTesting(true);
    setStripeTestResult(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/workspaces/settings/stripe/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ secretKey: stripeSecretKey }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Stripe connection test failed');
      }

      setStripeStatus('Connected');
      setStripeLastVerified(data.lastVerifiedAt || new Date().toISOString());
      setStripeTestResult({ success: true, message: `Connected! Default Currency: ${data.currency || 'USD'}` });
    } catch (err: any) {
      setStripeTestResult({ success: false, message: err.message || 'Stripe connection failed' });
    } finally {
      setStripeTesting(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!(await confirmDialog('Are you sure you want to disconnect Stripe for this Workspace? Applicants will no longer be able to make online payments.'))) return;
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/workspaces/settings/stripe/disconnect', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setStripePublishableKey('');
        setStripeSecretKey('');
        setStripeStatus('Not Connected');
        setStripeLastVerified(null);
        setStripeTestResult(null);
      }
    } catch (err) {
      console.error('Failed to disconnect Stripe:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-6" style={{ zIndex: z }} onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl h-[calc(100dvh-2rem)] sm:h-[calc(100dvh-3rem)] flex flex-col overflow-hidden animate-fade-in" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl btn-primary flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-black text-slate-900">Workspace Settings</p>
                <span className={`text-[11px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${ws?.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{ws?.status || '—'}</span>
              </div>
              <p className="text-xs text-slate-500">{ws ? `${ws.orgName || ws.name} · ${workspaceSubdomainHost(ws.slug)}` : 'Loading...'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveSettings} disabled={saving || !ws} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {saving ? 'Saving...' : 'Save'}
            </button>
            {saved && <span className="text-xs font-bold text-emerald-600">Saved ✓</span>}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer" aria-label="Close"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Tab nav */}
          <nav className="w-44 shrink-0 border-r border-slate-200 bg-slate-50/60 p-3 space-y-1 overflow-y-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${tab === t.key ? 'bg-white text-brand shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:bg-white/70'}`}>
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                  {t.key === 'roles' && <span className="ml-auto text-[11px] font-black text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">{activeRoles.length}/{MAX_CUSTOM_ROLES}</span>}
                </button>
              );
            })}
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0 overflow-y-auto p-5 lg:p-7">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-brand animate-spin" /></div>
            ) : tab === 'overview' ? (
              <Section title="Organization" subtitle="Basic identity details for this workspace.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Organization Name" value={orgName} onChange={setOrgName} placeholder={ws?.name || 'Organization'} />
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Workspace Slug</p>
                    <input value={ws?.slug || ''} disabled className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 text-slate-500 font-mono" />
                    <p className="text-[10px] text-slate-400 pt-1">Change it in the <button onClick={() => setTab('domain')} className="text-brand font-bold hover:underline cursor-pointer">Domains</button> tab.</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Plan</p>
                    <input value={ws?.plan || ''} disabled className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 text-slate-500 capitalize" />
                  </div>
                  <Field label="Country" value={country} onChange={setCountry} placeholder="United States" />
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Time Zone</p>
                    <select value={timezone} onChange={e => setTimezone(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none">{TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}</select>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Language</p>
                    <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none"><option value="en">English</option><option value="fr">Français</option><option value="es">Español</option></select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 pt-2">Status: <span className="font-bold capitalize">{ws?.status || '—'}</span> · Created {ws?.createdAt ? new Date(ws.createdAt).toLocaleDateString() : '—'}</p>
              </Section>
            ) : tab === 'branding' ? (
              <Section title="Brand Configuration" subtitle="These colors, logos, and name are applied to this workspace instantly.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {COLOR_FIELDS.map(f => (
                    <div key={f.field}>
                      <ColorField label={f.label} value={branding[f.field] as string} onChange={(v) => { setBranding(b => ({ ...b, [f.field]: v })); }} />
                    </div>
                  ))}
                </div>
                <Field label="Business Name (brand)" value={branding.businessName || ''} onChange={v => setBranding(b => ({ ...b, businessName: v }))} placeholder="ISATS Global" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <BrandingUploader label="Logo" size="h-14 w-14" url={branding.logoUrl} uploading={uploadingAsset === 'logo'} onPick={f => uploadBranding('logo', f)} onRemove={() => setBranding(b => ({ ...b, logoUrl: '' }))} />
                  <BrandingUploader label="Sidebar Logo" size="h-12 w-12" url={branding.sidebarLogoUrl} uploading={uploadingAsset === 'sidebar-logo'} onPick={f => uploadBranding('sidebar-logo', f)} onRemove={() => setBranding(b => ({ ...b, sidebarLogoUrl: '' }))} />
                  <BrandingUploader label="Favicon" size="h-10 w-10" url={branding.faviconUrl} uploading={uploadingAsset === 'favicon'} onPick={f => uploadBranding('favicon', f)} onRemove={() => setBranding(b => ({ ...b, faviconUrl: '' }))} />
                  <BrandingUploader label="Email Logo" size="h-12 w-12" url={branding.emailLogoUrl} uploading={uploadingAsset === 'email-logo'} onPick={f => uploadBranding('email-logo', f)} onRemove={() => setBranding(b => ({ ...b, emailLogoUrl: '' }))} />
                  <BrandingUploader label="Login Screen Logo" size="h-12 w-12" url={branding.loginLogoUrl} uploading={uploadingAsset === 'login-logo'} onPick={f => uploadBranding('login-logo', f)} onRemove={() => setBranding(b => ({ ...b, loginLogoUrl: '' }))} />
                  <BrandingUploader label="Invoice & Receipt Logo" size="h-12 w-12" url={branding.invoiceLogoUrl} uploading={uploadingAsset === 'invoice-logo'} onPick={f => uploadBranding('invoice-logo', f)} onRemove={() => setBranding(b => ({ ...b, invoiceLogoUrl: '' }))} />
                </div>
              </Section>
            ) : tab === 'domain' ? (
              <Section title="Domains" subtitle="Your default subdomain plus any custom domains.">
                <div className="space-y-2">
                  <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-200">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-slate-400" />
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Default Subdomain</p>
                      <span className="ml-auto text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">Default</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-sm font-bold text-slate-400 font-mono">https://</span>
                      <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="workspace" className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-brand-primary/20" />
                      <span className="text-sm font-bold text-slate-400 font-mono">.{getRootDomain()}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 flex items-center gap-1.5 pt-1.5">
                      <ShieldCheck className="h-3 w-3 shrink-0 text-emerald-500" />
                      Setting a new primary subdomain adds it to your portal — your original subdomain and every previous one keep working permanently, so existing links never break.
                    </p>
                    {(ws?.originalSlug || (ws?.slugAliases && ws.slugAliases.length > 0)) && (
                      <div className="pt-1.5 space-y-1">
                        {ws?.originalSlug && (
                          <p className="text-[11px] text-slate-500 font-mono">Original (permanent): {ws.originalSlug}.{getRootDomain()}</p>
                        )}
                        {(ws?.slugAliases || []).filter(a => a !== ws?.slug).map(a => (
                          <p key={a} className="text-[11px] text-slate-500 font-mono">Also resolves: {a}.{getRootDomain()}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  {domains.map(d => {
                    const rec = webDomains.find(w => w.domain === d);
                    const recStatus = String(rec?.status || '').toLowerCase();
                    return (
                      <div key={d} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-200">
                        <Globe className="h-4 w-4 text-slate-400" />
                        <span className="text-sm font-bold text-slate-700 font-mono">{d}</span>
                        {rec
                          ? <DomainStatusBadge status={recStatus} />
                          : <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">Legacy</span>}
                        <button onClick={() => detachWebDomain(d)} disabled={attachingWebDomain === d} className="ml-auto text-slate-400 hover:text-rose-500 cursor-pointer disabled:opacity-50" aria-label={`Remove ${d}`}>{attachingWebDomain === d ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}</button>
                      </div>
                    );
                  })}
                  {domains.length === 0 && <p className="text-xs text-slate-400 italic py-1 text-center">No custom domains attached yet. Verify a domain below, then attach it.</p>}
                </div>

                {/* Custom domain ownership verification */}
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Custom Domains — Ownership Verification</p>
                    <span className="text-[10px] font-bold text-slate-400">{webDomains.length} claimed</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    A custom domain can only be attached after you prove you control it: claim the domain, add the <b>TXT</b> record at your DNS provider, then click <b>Verify</b>. Once verified, attach it above.
                  </p>

                  {webDomainError && (
                    <div className="flex items-start gap-2 rounded-xl bg-rose-50/70 border border-rose-100 px-3.5 py-2.5 mt-3">
                      <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-rose-700 leading-snug">{webDomainError}</p>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2">
                    <input value={newWebDomain} onChange={e => setNewWebDomain(e.target.value)} placeholder="portal.example.com" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <button onClick={claimWebDomain} disabled={claimingWebDomain} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50">
                      {claimingWebDomain ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Claim domain
                    </button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {webDomains.map(d => {
                      const status = String(d.status || 'pending').toLowerCase();
                      const attached = domains.includes(d.domain);
                      return (
                        <div key={d.id} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <Globe className="h-4 w-4 text-slate-400 shrink-0" />
                              <span className="text-xs font-black text-slate-800 font-mono">{d.domain}</span>
                              <DomainStatusBadge status={status} />
                              {attached && <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">Attached</span>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {status === 'verified' && !attached && (
                                <button onClick={() => attachWebDomain(d.domain)} disabled={attachingWebDomain === d.domain} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg btn-primary text-white text-[10px] font-bold cursor-pointer disabled:opacity-50">
                                  {attachingWebDomain === d.domain ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Attach
                                </button>
                              )}
                              <button onClick={() => verifyWebDomainRec(d.id)} disabled={verifyingWebDomain === d.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-[10px] font-bold text-slate-600 hover:bg-white cursor-pointer disabled:opacity-50">
                                {verifyingWebDomain === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {status === 'verified' ? 'Re-verify' : 'Verify'}
                              </button>
                              <button onClick={() => releaseWebDomain(d.id, d.domain)} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 cursor-pointer" aria-label={`Release ${d.domain}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>
                          <div className="px-4 py-3 space-y-2.5">
                            <DnsRecordRow type="TXT · Ownership" host={d.verificationHost} value={d.token} />
                            {status === 'lost' && (
                              <p className="text-[11px] text-amber-600 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 shrink-0" /> The TXT record is no longer visible in DNS. The domain stays attached for now, but it cannot be re-attached or newly attached until the record is restored and re-verified.</p>
                            )}
                            {status === 'verified' && !attached && (
                              <p className="text-[11px] text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 shrink-0" /> Ownership verified — click Attach to serve this workspace on {d.domain}. Keep the TXT record in place; verification is re-checked when you click Re-verify.</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {webDomains.length === 0 && <p className="text-xs text-slate-400 italic py-2 text-center">No domains claimed yet. Claim one above to start verification.</p>}
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 flex items-center gap-1.5 pt-1"><Info className="h-3 w-3" /> Custom domains map to the portal URL (e.g. portal.yourcompany.com). Sending domains for email are managed in <button onClick={() => setTab('email')} className="text-brand font-bold hover:underline cursor-pointer">Email Sender</button>.</p>
              </Section>
            ) : tab === 'agents' ? (
              /* TEAMS & ROLES: this section manages the WORKSPACE TEAM (internal staff) only — organization teams live in their org dashboards — see the canonical note above isOrgConsoleProfile in serverWorkspaces.ts. */
              <Section title="Workspace Team" subtitle="Workspace team members, their roles and access status.">
                <div className="flex items-start gap-2 rounded-xl bg-blue-50/70 border border-blue-100 px-3.5 py-2.5">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-blue-700 leading-snug">
                    <b>Team members</b> (admins, managers, team members, viewers, custom roles) are managed here. The
                    workspace <b>owner</b> and <b>referrer agents</b> are shown for reference only — their roles are
                    protected. <b>Employers</b> and <b>sponsors</b> live in their own consoles and never appear in this list.
                    Role changes apply on every device immediately and are enforced by the backend.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-slate-600 bg-slate-100 rounded-lg px-2.5 py-1">
                    <Users className="h-3.5 w-3.5" /> Members <b className="text-slate-900">{teamRows.length + privilegedRows.length}</b>
                  </span>
                  {agentRows.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-indigo-600 bg-indigo-50 rounded-lg px-2.5 py-1">
                      <Briefcase className="h-3.5 w-3.5" /> Referrer Agents <b className="text-indigo-700">{agentRows.length}</b>
                    </span>
                  )}
                  {(pendingInviteCount + pendingInvitations.length) > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-amber-600 bg-amber-50 rounded-lg px-2.5 py-1">
                      <Clock className="h-3.5 w-3.5" /> Invites Pending <b className="text-amber-700">{pendingInviteCount + pendingInvitations.length}</b>
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  {teamRows.map(({ m, bucket }) => {
                    const isSelf = m.uid === currentUid;
                    const isArchived = bucket === 'archived-role';
                    const roleEditable = !isArchived && editableWorkspaceRole(m.role);
                    // 'team_member' is a legacy alias of 'member' — normalize so
                    // the select value always matches an option.
                    const shownRoleValue = m.role === 'team_member' ? 'member' : m.role;
                    return (
                      <div key={m.uid} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-3 bg-white">
                        {m.profilePhotoUrl ? (
                          <img src={m.profilePhotoUrl} alt="" className="h-9 w-9 rounded-full object-cover border border-slate-200 shrink-0" />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-brand/10 text-brand flex items-center justify-center text-[11px] font-black shrink-0 uppercase">
                            {(m.name || m.email || '?').slice(0, 2)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="text-xs font-bold text-slate-800 truncate">{m.name || '—'}</p>
                            {isSelf && <span className="shrink-0 text-[11px] font-black text-brand bg-brand/10 rounded-full px-1.5 py-0.5">You</span>}
                          </div>
                          <p className="text-[10px] text-slate-400 truncate">{m.email}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {m.hasPassword ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-emerald-600 bg-emerald-50 rounded-md px-1.5 py-0.5">
                                <UserCheck className="h-3 w-3" /> Active
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 text-[11px] font-black uppercase rounded-md px-1.5 py-0.5 ${m.invitePending ? 'text-amber-600 bg-amber-50' : 'text-slate-500 bg-slate-50'}`}>
                                <Mail className="h-3 w-3" /> {m.invitePending ? 'Invite Pending' : 'Set Up Password'}
                              </span>
                            )}
                            {isArchived && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-rose-600 bg-rose-50 rounded-md px-1.5 py-0.5">
                                <AlertTriangle className="h-3 w-3" /> Role Archived
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {roleEditable ? (
                            <select
                              value={shownRoleValue}
                              onChange={e => changeRole(m.uid, e.target.value)}
                              className="max-w-[110px] sm:max-w-[150px] truncate border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] font-bold bg-white text-slate-600 outline-none focus:ring-2 focus:ring-brand-primary/20"
                            >
                              {roleOptions.map(r => <option key={r} value={r}>{roleDisplayLabel(r)}</option>)}
                            </select>
                          ) : isArchived ? (
                            <select
                              value=""
                              onChange={e => { if (e.target.value) changeRole(m.uid, e.target.value); }}
                              className="max-w-[140px] truncate border border-dashed border-rose-200 rounded-lg px-2 py-1.5 text-[10px] font-bold bg-rose-50/40 text-rose-500 outline-none focus:ring-2 focus:ring-rose-500/20"
                            >
                              <option value="" disabled>Reassign role…</option>
                              {roleOptions.map(r => <option key={r} value={r}>{roleDisplayLabel(r)}</option>)}
                            </select>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-[10px] font-black">{roleDisplayLabel(m.role)}</span>
                          )}
                          {!isSelf && (
                            <div className="flex items-center gap-0.5">
                              {!m.hasPassword && (
                                <button onClick={() => resendInvite(m.uid)} className="p-1.5 rounded-lg text-slate-300 hover:text-brand hover:bg-brand/5 cursor-pointer" aria-label={`Resend invite to ${m.email}`} title="Resend welcome email with a fresh password setup link"><RefreshCw className="h-4 w-4" /></button>
                              )}
                              <button onClick={() => removeMember(m.uid)} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 cursor-pointer" aria-label={`Remove ${m.email}`} title="Remove from workspace"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {teamRows.length === 0 && privilegedRows.length === 0 && (
                    <p className="text-xs text-slate-400 italic py-3 text-center">No workspace team members yet. Invite the first member below.</p>
                  )}
                </div>

                {pendingInvitations.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pt-1">Pending invitations</p>
                    {pendingInvitations.map((inv: any) => (
                      <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/40 px-3.5 py-3">
                        <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-[11px] font-black shrink-0 uppercase">
                          {(inv.email || '?').slice(0, 2)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate">{inv.email}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-amber-600 bg-amber-50 rounded-md px-1.5 py-0.5">
                              <Mail className="h-3 w-3" /> Invitation Pending
                            </span>
                            <span className="text-[10px] text-slate-400">
                              as {roleDisplayLabel(inv.memberRole || 'member')}
                              {inv.expiresAt ? ` · expires ${new Date(inv.expiresAt).toLocaleDateString()}` : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button
                            onClick={() => resendTeamInvitation(inv.id, inv.email)}
                            disabled={invitationBusyId === inv.id}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-brand hover:bg-brand/5 cursor-pointer disabled:opacity-50"
                            aria-label={`Resend invitation to ${inv.email}`}
                            title="Resend invitation email with a fresh link"
                          >
                            {invitationBusyId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => cancelTeamInvitation(inv.id, inv.email)}
                            disabled={invitationBusyId === inv.id}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 cursor-pointer disabled:opacity-50"
                            aria-label={`Cancel invitation for ${inv.email}`}
                            title="Cancel invitation"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(privilegedRows.length > 0 || agentRows.length > 0) && (
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pt-1">Other workspace accounts</p>
                    {privilegedRows.map(({ m }) => {
                      const isPrimaryOwner = !!ws && ws.primaryAdminUid === m.uid;
                      return (
                        <div key={m.uid} className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/40 px-3.5 py-2.5">
                          <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-[11px] font-black shrink-0 uppercase">
                            {(m.name || m.email || '?').slice(0, 2)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-slate-800 truncate">
                              {m.name || '—'}
                              {m.uid === currentUid && <span className="ml-1.5 text-[11px] font-black text-brand bg-brand/10 rounded-full px-1.5 py-0.5">You</span>}
                            </p>
                            <p className="text-[10px] text-slate-400 truncate">{m.email}</p>
                            <p className="text-[10px] text-amber-600/80 truncate">{isPrimaryOwner ? 'Primary workspace owner — protected role' : 'Protected workspace role — not editable here'}</p>
                          </div>
                          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md shrink-0">
                            <ShieldCheck className="h-3 w-3" /> {roleDisplayLabel(m.role)}
                          </span>
                        </div>
                      );
                    })}
                    {agentRows.map(({ m }) => (
                      <div key={m.uid} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5">
                        <div className="h-8 w-8 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center text-[11px] font-black shrink-0 uppercase">
                          {(m.name || m.email || '?').slice(0, 2)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate">
                            {m.name || '—'}
                            {m.uid === currentUid && <span className="ml-1.5 text-[11px] font-black text-brand bg-brand/10 rounded-full px-1.5 py-0.5">You</span>}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate">{m.email}</p>
                          <p className="text-[10px] text-indigo-500/70 truncate">Referrer agent — access & role are managed in the Referrers console</p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md shrink-0">
                          <Briefcase className="h-3 w-3" /> Referrer Agent
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t border-slate-100 pt-3 space-y-2.5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Invite Workspace Team Member</p>
                  <div className="flex items-center gap-2">
                    <input value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} placeholder="teammate@company.com" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <select value={newMemberRole} onChange={e => setNewMemberRole(e.target.value)} className="max-w-[120px] sm:max-w-[160px] truncate border border-slate-200 rounded-xl px-2.5 py-2 text-xs font-bold bg-white text-slate-600 outline-none focus:ring-2 focus:ring-brand-primary/20">
                      {addRoleOptions.map(r => <option key={r} value={r}>{roleDisplayLabel(r)}</option>)}
                    </select>
                    <button onClick={addMember} disabled={addingMember} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50">
                      {addingMember ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      <span>{addingMember ? 'Adding...' : 'Add'}</span>
                    </button>
                  </div>
                  {addOutcome && (
                    <div
                      role={addOutcome.kind === 'error' ? 'alert' : 'status'}
                      className={`flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-[11px] leading-snug ${addOutcome.kind === 'error' ? 'bg-rose-50/70 border-rose-100 text-rose-700' : 'bg-emerald-50/70 border-emerald-100 text-emerald-700'}`}
                    >
                      {addOutcome.kind === 'error' ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <UserCheck className="h-4 w-4 mt-0.5 shrink-0" />}
                      <p>{addOutcome.message}</p>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400">Adding an email sends an invitation — the person joins this workspace's team when they accept it (from the email link, or after signing in here). They don't need an account yet. Employer and sponsor accounts are managed in their own consoles and can't be team members, and an email with an Applicant account in this workspace can't also be a team member. Custom roles come from the <button onClick={() => setTab('roles')} className="text-brand font-bold hover:underline cursor-pointer">Custom Roles</button> tab.</p>
                </div>
              </Section>
            ) : tab === 'screening-staff' ? (
              <Section title="Screening Staff & Auto-Assignment" subtitle="Designate team members who conduct applicant screenings, set interview durations, and manage auto-assignment rules.">
                {loadingScreeningStaff ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 text-brand animate-spin" /></div>
                ) : (
                  <div className="space-y-6">
                    {/* Screening Event Mode: Collective vs Round Robin */}
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">Screening Interview Mode</span>
                        <span className="text-[11px] text-slate-500">
                          Choose how designated screening admins/hosts are assigned and scheduled for public screening bookings.
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div
                          onClick={() => setScreeningType('collective')}
                          className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                            screeningType === 'collective'
                              ? 'border-brand bg-brand/5 shadow-sm'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-slate-900">Collective Screening</span>
                            <input
                              type="radio"
                              name="screeningType"
                              checked={screeningType === 'collective'}
                              onChange={() => setScreeningType('collective')}
                              className="text-brand focus:ring-brand h-4 w-4"
                            />
                          </div>
                          <p className="text-[11px] text-slate-500 leading-relaxed">
                            Requires ALL assigned host admins to attend the same meeting. Time slots are shown only when everyone is free at the same time.
                          </p>
                        </div>

                        <div
                          onClick={() => setScreeningType('round_robin')}
                          className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                            screeningType === 'round_robin'
                              ? 'border-brand bg-brand/5 shadow-sm'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-slate-900">Round Robin Screening</span>
                            <input
                              type="radio"
                              name="screeningType"
                              checked={screeningType === 'round_robin'}
                              onChange={() => setScreeningType('round_robin')}
                              className="text-brand focus:ring-brand h-4 w-4"
                            />
                          </div>
                          <p className="text-[11px] text-slate-500 leading-relaxed">
                            Rotates meetings among multiple assigned host admins based on individual availability to distribute workload and connect with the next available person.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Auto-Assign Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-200">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">Auto Assign on Booking</span>
                        <span className="text-[11px] text-slate-500">
                          When ON, public screening bookings automatically assign host(s) based on selected screening mode and designated staff.
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={autoAssignOnBooking}
                          onChange={e => setAutoAssignOnBooking(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-success" />
                      </label>
                    </div>

                    {/* How Far in Advance & Duration & Location */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                        <label className="text-xs font-bold text-slate-800 block">Advance Booking Constraint</label>
                        <p className="text-[11px] text-slate-500">
                          Set how far in advance screening interviews can be scheduled.
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={1}
                            max={365}
                            value={advanceBookingAmount}
                            onChange={e => setAdvanceBookingAmount(Math.max(1, Number(e.target.value) || 1))}
                            className="w-20 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 bg-white font-bold text-slate-800"
                          />
                          <select
                            value={advanceBookingUnit}
                            onChange={e => setAdvanceBookingUnit(e.target.value as 'days' | 'weeks' | 'months')}
                            className="flex-1 border border-slate-200 rounded-xl px-2.5 py-2 text-xs font-bold bg-white text-slate-700 outline-none"
                          >
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </select>
                        </div>
                      </div>
                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                        <div>
                          <label className="text-xs font-bold text-slate-800 block mb-0.5">Screening Event Duration</label>
                          <p className="text-[11px] text-slate-500">
                            Select preset duration or manually specify exact duration in minutes.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] font-bold text-slate-500 block mb-1">Preset Options</label>
                            <select
                              value={[5, 10, 15, 20, 25, 30, 45, 60, 90, 120].includes(screeningDurationMinutes) ? screeningDurationMinutes : 'custom'}
                              onChange={e => {
                                if (e.target.value !== 'custom') {
                                  setScreeningDurationMinutes(Number(e.target.value));
                                }
                              }}
                              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-700 font-bold outline-none"
                            >
                              <option value={5}>5 Minutes</option>
                              <option value={10}>10 Minutes</option>
                              <option value={15}>15 Minutes</option>
                              <option value={20}>20 Minutes</option>
                              <option value={25}>25 Minutes</option>
                              <option value={30}>30 Minutes</option>
                              <option value={45}>45 Minutes</option>
                              <option value={60}>60 Minutes (1 Hour)</option>
                              <option value={90}>90 Minutes (1.5 Hours)</option>
                              <option value={120}>120 Minutes (2 Hours)</option>
                              <option value="custom">Custom Duration...</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-slate-500 block mb-1">Duration (Minutes)</label>
                            <input
                              type="number"
                              min={1}
                              max={480}
                              value={screeningDurationMinutes || ''}
                              onChange={e => setScreeningDurationMinutes(Math.max(1, parseInt(e.target.value) || 0))}
                              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-800 font-bold outline-none"
                              placeholder="e.g. 5, 10, 15, 25"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                        <label className="text-xs font-bold text-slate-800 block">Screening Destination & Meeting Link</label>
                        <p className="text-[11px] text-slate-500">
                          Select the video platform or destination link generated for screening appointments.
                        </p>
                        <select
                          value={screeningLocationType}
                          onChange={e => setScreeningLocationType(e.target.value as any)}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-700 font-bold outline-none"
                        >
                          <option value="daily">Daily.co (Auto-Generated Meeting Link)</option>
                          <option value="jitsi">Jitsi Meet (Auto-Generated Meeting Link)</option>
                          <option value="google_meet">Google Meet (Custom Link)</option>
                          <option value="zoom">Zoom (Custom Link)</option>
                          <option value="custom_url">Custom Video URL</option>
                        </select>
                        {['google_meet', 'zoom', 'custom_url'].includes(screeningLocationType) && (
                          <input
                            type="text"
                            value={screeningLocationDetails}
                            onChange={e => setScreeningLocationDetails(e.target.value)}
                            placeholder="e.g. https://meet.google.com/abc-defg-hij"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-800 outline-none font-mono"
                          />
                        )}
                      </div>
                    </div>

                    {/* Public Screening Scheduling Link */}
                    <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-200 space-y-2">
                      <label className="text-xs font-bold text-indigo-950 block">Public Screening Scheduling Link</label>
                      <p className="text-[11px] text-indigo-700">
                        Share this public link with eligible applicants so they can schedule a screening interview without logging in.
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={`${window.location.origin}/book-screening`}
                          className="flex-1 px-3 py-2 text-xs font-mono border border-indigo-200 rounded-xl bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/book-screening`);
                            notifyToast.success('Public Screening Scheduling Link copied to clipboard!');
                          }}
                          className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shrink-0 shadow-sm"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Copy Link
                        </button>
                      </div>
                    </div>

                    {/* Screening Staff Designation */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-800">Designated Screening Staff</p>
                          <p className="text-[11px] text-slate-500">Only designated members are considered for public screening assignments and slot availability calculations.</p>
                        </div>
                        <span className="text-[10px] font-black text-brand bg-brand/10 px-2.5 py-1 rounded-lg">
                          {screeningStaffUids.length} Designated
                        </span>
                      </div>

                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {eligibleScreeningMembers.map(m => {
                          const isDesignated = screeningStaffUids.includes(m.uid);
                          const isBookingAccessOn = m.bookingAccessEnabled !== false;

                          const toggleMemberBookingAccess = async (e: React.MouseEvent, targetUid: string, currentVal: boolean) => {
                            e.stopPropagation();
                            try {
                              const newVal = !currentVal;
                              await safeFetch(`/api/workspaces/members/${targetUid}/booking-access`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ bookingAccessEnabled: newVal }),
                              });
                              setEligibleScreeningMembers(prev => prev.map(item => item.uid === targetUid ? { ...item, bookingAccessEnabled: newVal } : item));
                              notifyToast.success(`Booking access ${newVal ? 'enabled' : 'disabled'} for ${m.name || m.email}`);
                            } catch (err: any) {
                              notifyToast('error', err.message || 'Failed to update booking access');
                            }
                          };

                          return (
                            <div
                              key={m.uid}
                              onClick={() => toggleScreeningStaffUid(m.uid)}
                              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-3.5 rounded-xl border transition-all cursor-pointer gap-2 ${
                                isDesignated ? 'border-brand/40 bg-brand/5' : 'border-slate-200 bg-white hover:bg-slate-50'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-black uppercase shrink-0 ${
                                  isDesignated ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {(m.name || m.email || '?').slice(0, 2)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-slate-800 truncate">{m.name || 'Unnamed'}</p>
                                  <p className="text-[10px] text-slate-400 truncate">{m.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                                {/* Direct Booking Page Status Toggle (Independent from Screening Eligibility) */}
                                <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={e => toggleMemberBookingAccess(e, m.uid, isBookingAccessOn)}
                                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition flex items-center gap-1 cursor-pointer ${
                                      isBookingAccessOn
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                        : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                    }`}
                                    title="Direct Booking Page status (independent of Screening eligibility)"
                                  >
                                    <span className={`h-1.5 w-1.5 rounded-full ${isBookingAccessOn ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                    Direct Link: {isBookingAccessOn ? 'ON' : 'OFF'}
                                  </button>
                                </div>

                                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded capitalize">
                                  {m.role || 'Member'}
                                </span>
                                <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-bold text-slate-700 bg-white px-2 py-1 rounded border border-slate-200" title="Screening Interview Eligibility (independent of Direct Booking link)">
                                  <span>Screening:</span>
                                  <input
                                    type="checkbox"
                                    checked={isDesignated}
                                    onChange={() => {}}
                                    className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand cursor-pointer"
                                  />
                                </label>
                              </div>
                            </div>
                          );
                        })}
                        {eligibleScreeningMembers.length === 0 && (
                          <p className="text-xs text-slate-400 italic py-4 text-center">No team members available to designate as Screening Staff.</p>
                        )}
                      </div>
                    </div>

                    <div className="pt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={saveScreeningStaffSettings}
                        disabled={savingScreeningStaff}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50 shadow-md"
                      >
                        {savingScreeningStaff ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        {savingScreeningStaff ? 'Saving...' : 'Save Screening Staff Settings'}
                      </button>
                    </div>
                  </div>
                )}
              </Section>
            ) : tab === 'roles' ? (
              <Section title="Custom Roles" subtitle={`Create up to ${MAX_CUSTOM_ROLES} roles. Each module can be set to None, View or Edit — permissions are enforced server-side.`}>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5">
                  <span className="text-xs font-bold text-slate-700">Roles used</span>
                  <span className={`text-xs font-black ${activeRoles.length >= MAX_CUSTOM_ROLES ? 'text-amber-600' : 'text-emerald-600'}`}>{activeRoles.length} / {MAX_CUSTOM_ROLES}</span>
                </div>

                <div className="space-y-3">
                  {activeRoles.map(role => {
                    const isRoleExpanded = !!expandedRoles[role.roleId];
                    return (
                      <div key={role.roleId} className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-2xs transition-all">
                        <div
                          onClick={() => toggleRoleExpand(role.roleId)}
                          className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100/80 cursor-pointer transition-all border-b border-slate-100"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="h-8 w-8 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                              <UserCog className="h-4 w-4 text-brand" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-black text-slate-800 truncate block">{role.name}</span>
                                {roleMemberCount(role.name) > 0 && (
                                  <span className="shrink-0 inline-flex items-center text-[11px] font-black text-brand bg-brand/10 rounded-full px-1.5 py-0.5">
                                    <Users className="h-2.5 w-2.5 mr-0.5" /> {roleMemberCount(role.name)} member{roleMemberCount(role.name) === 1 ? '' : 's'}
                                  </span>
                                )}
                              </div>
                              <span className="text-[10px] text-slate-500 font-medium">Click to {isRoleExpanded ? 'collapse' : 'expand and configure permissions'}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); archiveRole(role.roleId); }}
                              className="inline-flex items-center gap-1 text-xs font-bold text-rose-500 hover:text-rose-600 cursor-pointer shrink-0 px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Archive
                            </button>
                            <button
                              type="button"
                              className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-white cursor-pointer"
                            >
                              {isRoleExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>

                        {isRoleExpanded && (
                          <div className="p-4 bg-white border-t border-slate-100">
                            <CustomRolePermissionsEditor
                              roleName={role.name}
                              permissions={role.permissions || {}}
                              onChange={updatedPerms => updateRoleFullPermissions(role.roleId, updatedPerms)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {activeRoles.length === 0 && <p className="text-xs text-slate-400 italic py-3 text-center">No custom roles yet. Create one below.</p>}
                </div>

                {activeRoles.length >= MAX_CUSTOM_ROLES ? (
                  <p className="text-[11px] text-amber-600 font-bold flex items-center gap-1.5 pt-1"><Info className="h-3.5 w-3.5" /> Limit reached. Archive a role to create a new one.</p>
                ) : (
                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-700">Create New Custom Role</p>
                    <Field label="Role Name" value={roleName} onChange={setRoleName} placeholder="e.g. Case Officer" />

                    <CustomRolePermissionsEditor
                      roleName={roleName || 'New Role'}
                      permissions={rolePerms}
                      onChange={setRolePerms}
                    />

                    <button onClick={createRole} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer shadow-md"><Plus className="h-4 w-4" /> Save New Role</button>
                    <p className="text-[10px] text-slate-400">Assign roles to members from the <button onClick={() => setTab('agents')} className="text-brand font-bold hover:underline cursor-pointer">Team</button> tab (choose the role name in the role selector).</p>
                  </div>
                )}
              </Section>
            ) : tab === 'signup' ? (
              <Section title="Signup Roles" subtitle="Decide which roles can self-register on this workspace's portal.">
                <p className="text-xs text-slate-500">Uncheck a role to prevent new accounts from signing up as that role here. Admins can still create them manually.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                  {SIGNUP_ROLE_OPTIONS.map(opt => {
                    const on = signupRoles.length === 0 || signupRoles.includes(opt.key);
                    return (
                      <button key={opt.key} onClick={() => toggleSignupRole(opt.key)} className={`flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all cursor-pointer ${on ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                        <span className={`mt-0.5 h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${on ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 bg-white'}`}>
                          {on && <span className="text-white text-[10px] font-black">✓</span>}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-xs font-black text-slate-800">{opt.label}</span>
                          <span className="block text-[10px] text-slate-500 mt-0.5 leading-snug">{opt.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {signupRoles.length === 0 && <p className="text-[11px] text-slate-400 pt-2 italic">All roles allowed (no restriction set).</p>}

                {/* Direct Role Signup Links */}
                <div className="border-t border-slate-100 pt-5 mt-4 space-y-3">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-700">Direct Role Signup Links</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Share these dedicated signup URLs with prospective users to route them directly into the appropriate onboarding flow.</p>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5 pt-1">
                    {[
                      { key: 'applicant', label: 'Applicant Signup', desc: 'Direct onboarding for prospective exchange visitors & candidates', roleKey: 'applicant' },
                      { key: 'referrer', label: 'Referrer Signup', desc: 'Dedicated signup link for authorized referral partners & agencies', roleKey: 'referrer' },
                      { key: 'employer', label: 'Employer Signup', desc: 'Dedicated registration link for host organizations & employers', roleKey: 'employer' },
                      { key: 'sponsor', label: 'Rep / Sponsor Signup', desc: 'Dedicated onboarding link for sponsoring organization representatives', roleKey: 'sponsor' },
                    ].map(roleItem => {
                      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
                      const signupUrl = `${baseUrl}/signup?role=${roleItem.roleKey}`;
                      const isCopied = copiedLinkKey === roleItem.key;

                      return (
                        <div key={roleItem.key} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3.5 rounded-xl border border-slate-200 bg-white shadow-xs">
                          <div className="min-w-0 flex-1">
                            <span className="block text-xs font-bold text-slate-900">{roleItem.label}</span>
                            <span className="block text-[11px] text-slate-500 mt-0.5">{roleItem.desc}</span>
                            <span className="block text-[10px] font-mono text-brand bg-slate-50 border border-slate-200 rounded px-2 py-1 mt-1.5 truncate max-w-full">
                              {signupUrl}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(signupUrl);
                              setCopiedLinkKey(roleItem.key);
                              notifyToast('success', `${roleItem.label} link copied to clipboard!`);
                              setTimeout(() => setCopiedLinkKey(null), 2000);
                            }}
                            className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 border ${
                              isCopied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                            }`}
                          >
                            {isCopied ? (
                              <>
                                <Check className="h-3.5 w-3.5 text-emerald-600" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="h-3.5 w-3.5 text-slate-500" />
                                <span>Copy Link</span>
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Section>
            ) : tab === 'email' ? (
              <Section title="Email Sender Identity" subtitle="The name and address used when the portal sends emails on behalf of this workspace.">
                {(() => {
                  const hasVerifiedSendingDomain = emailDomains.some(d => d.status === 'verified' || d.status === 'active');
                  return (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Sender Name" value={emailSenderName} onChange={setEmailSenderName} placeholder={ws?.name || 'Workspace'} />
                        <Field
                          label="Sender Email"
                          value={emailSenderEmail}
                          onChange={setEmailSenderEmail}
                          placeholder="noreply@edutrouve.org"
                          disabled={!hasVerifiedSendingDomain}
                          helpText={!hasVerifiedSendingDomain ? "Locked. Add and authenticate a sending domain below to modify." : undefined}
                        />
                      </div>
                      <p className="text-[11px] text-slate-400 pt-1 flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> Used for transactional emails, notifications, and OTPs. New workspaces default to <b>{ws?.name || 'the workspace name'}</b> &lt;noreply@edutrouve.org&gt;.</p>
                    </>
                  );
                })()}

                {/* Sending domains */}
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sending Domains</p>
                    <span className="text-[10px] font-bold text-slate-400">{emailDomains.length} domain{emailDomains.length !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Add a domain, add the DKIM <b>TXT</b> + bounce <b>CNAME</b> records at your DNS provider, then click <b>Verify</b>. Once verified, emails can be sent from <b>@yourdomain.com</b>.
                  </p>

                  {!providerConfigured && (
                    <div className="flex items-start gap-2 rounded-xl bg-amber-50/70 border border-amber-100 px-3.5 py-2.5 mt-3">
                      <Info className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-amber-700 leading-snug">The email domain service is not configured on this deployment yet. Domains can still be saved, but DKIM/CNAME records and verification require the platform admin to enable ZeptoMail domain APIs.</p>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2">
                    <input value={newSendingDomain} onChange={e => setNewSendingDomain(e.target.value)} placeholder="example.com" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <input value={newBouncePrefix} onChange={e => setNewBouncePrefix(e.target.value)} placeholder="bounce" className="w-24 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono bg-white outline-none focus:ring-2 focus:ring-brand-primary/20" title="Bounce subdomain prefix" />
                    <button onClick={addSendingDomain} disabled={addingDomain} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50">
                      {addingDomain ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                    </button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {emailDomains.map(d => {
                      const dkim = d.dnsRecords?.dkim;
                      const cname = d.dnsRecords?.cname;
                      const status = String(d.status || 'unverified').toLowerCase();
                      return (
                        <div key={d.id} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <Globe className="h-4 w-4 text-slate-400 shrink-0" />
                              <span className="text-xs font-black text-slate-800 font-mono">{d.domain}</span>
                              <DomainStatusBadge status={status} />
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => verifySendingDomain(d.id)} disabled={verifyingDomain === d.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-[10px] font-bold text-slate-600 hover:bg-white cursor-pointer disabled:opacity-50">
                                {verifyingDomain === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Verify
                              </button>
                              <button onClick={() => deleteSendingDomain(d.id, d.domain)} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 cursor-pointer" aria-label={`Remove ${d.domain}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>

                          {(dkim || cname) && (
                            <div className="px-4 py-3 space-y-2.5">
                              {dkim?.record_name && (
                                <DnsRecordRow type="TXT · DKIM" host={dkim.record_name} value={dkim.record_value} />
                              )}
                              {cname?.record_name && (
                                <DnsRecordRow type="CNAME · Bounce" host={cname.record_name} value={cname.record_value} />
                              )}
                              {!dkim?.record_name && !cname?.record_name && (
                                <p className="text-[10px] text-slate-400 italic">DNS records become available after ZeptoMail provisions the domain.</p>
                              )}
                            </div>
                          )}

                          {agents.length > 0 && (
                            <div className="px-4 py-3 border-t border-slate-100">
                              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Mail Agents</p>
                              <div className="flex flex-wrap gap-2">
                                {agents.map(a => {
                                  const on = (d.mailagentKeys || []).includes(a.key);
                                  return (
                                    <button key={a.key} onClick={() => toggleAgentOnDomain(d.id, a.key)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-bold cursor-pointer transition-all ${on ? 'border-brand/40 bg-brand/10 text-brand' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                                      <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-brand' : 'bg-slate-300'}`} />
                                      {a.name || a.key}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {emailDomains.length === 0 && <p className="text-xs text-slate-400 italic py-2 text-center">No sending domains yet. Add one above to start verification.</p>}
                  </div>
                </div>
              </Section>
            ) : tab === 'stripe' ? (
              <Section title="Finance / Payments — Stripe Configuration" subtitle="Configure unique Stripe account credentials to process online payments and issue invoice payment links for candidates in this workspace.">
                <div className="space-y-6">
                  {/* Connection Status Card */}
                  <div className={`p-4 rounded-2xl border flex items-center justify-between ${stripeStatus === 'Connected' ? 'bg-emerald-50/70 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-lg ${stripeStatus === 'Connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                        <DollarSign className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900">Stripe Integration Status:</span>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${stripeStatus === 'Connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                            {stripeStatus === 'Connected' ? 'Connected ✓' : 'Not Connected'}
                          </span>
                        </div>
                        {stripeLastVerified && (
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            Last verified: {new Date(stripeLastVerified).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {stripeStatus === 'Connected' && (
                      <button
                        type="button"
                        onClick={handleDisconnectStripe}
                        className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-xl text-xs transition cursor-pointer border border-rose-200"
                      >
                        Disconnect Stripe
                      </button>
                    )}
                  </div>

                  {stripeTestResult && (
                    <div className={`p-3 rounded-xl border text-xs font-bold flex items-center gap-2 ${stripeTestResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                      {stripeTestResult.success ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-rose-600" />}
                      <span>{stripeTestResult.message}</span>
                    </div>
                  )}

                  {/* Step-by-step Setup Guide */}
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3 text-xs text-slate-700">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-brand" /> Step-by-Step Stripe & Webhook Setup
                    </h4>
                    <ol className="list-decimal list-inside space-y-1.5 font-medium leading-relaxed">
                      <li>Enter your <strong>Stripe Publishable Key</strong> and <strong>Secret Key</strong> below and click <strong>Save & Verify Connection</strong>.</li>
                      <li>In your Stripe Dashboard, navigate to <strong>Developers → Webhooks</strong> and click <strong>Add endpoint</strong>.</li>
                      <li>Copy the Webhook URL below and paste it as the Endpoint URL:
                        <div className="mt-1 flex items-center gap-2">
                          <code className="px-2.5 py-1 bg-white border border-slate-300 rounded font-mono text-[11px] text-slate-900 select-all font-bold">
                            {window.location.origin}/api/webhooks/stripe
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/stripe`);
                              notifyToast.success('Webhook URL copied to clipboard');
                            }}
                            className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded font-bold text-[10px]"
                          >
                            Copy URL
                          </button>
                        </div>
                      </li>
                      <li>Select events: <strong><code className="text-brand">checkout.session.completed</code></strong> and <strong><code className="text-brand">payment_intent.succeeded</code></strong>.</li>
                      <li>Test payment links in portal to confirm auto-allocation against candidate invoices.</li>
                    </ol>
                  </div>

                  {/* Credentials Form */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Stripe API Credentials</h4>

                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Stripe Publishable Key (pk_live_... / pk_test_...)</label>
                        <input
                          type="text"
                          value={stripePublishableKey}
                          onChange={(e) => setStripePublishableKey(e.target.value)}
                          placeholder="pk_live_51..."
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-brand/20 outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Stripe Secret Key (sk_live_... / sk_test_...)</label>
                        <input
                          type="password"
                          value={stripeSecretKey}
                          onChange={(e) => setStripeSecretKey(e.target.value)}
                          placeholder="sk_live_51..."
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-brand/20 outline-none"
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                          Secret keys are encrypted and masked ("••••••••••••••••••••") for security.
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <button
                        type="button"
                        disabled={stripeTesting || (!stripeSecretKey && !stripePublishableKey)}
                        onClick={handleTestStripeConnection}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer disabled:opacity-50"
                      >
                        {stripeTesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-indigo-600" />}
                        {stripeTesting ? 'Testing Connection...' : 'Test Connection'}
                      </button>

                      <button
                        type="button"
                        disabled={saving}
                        onClick={saveStripeSettings}
                        className="inline-flex items-center gap-1.5 px-5 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-xl text-xs transition cursor-pointer shadow-md shadow-emerald-500/20 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save Credentials'}
                      </button>
                    </div>
                  </div>
                </div>
              </Section>
            ) : tab === 'llm' ? (
              <Section title="Call LLM Model" subtitle="Configure custom Large Language Model (LLM) providers and parameters for workspace-level AI assist, resume parsing, and document validation.">
                {/* Basic / Advanced Tab Header */}
                <div className="flex border-b border-slate-200 mb-4 bg-slate-50 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setLlmTab('basic')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${llmTab === 'basic' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    <Cpu className="h-3.5 w-3.5" />
                    <span>Basic Configuration</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLlmTab('advanced')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${llmTab === 'advanced' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    <Sliders className="h-3.5 w-3.5" />
                    <span>Advanced Configuration</span>
                  </button>
                </div>

                {llmTab === 'basic' ? (
                  <div className="space-y-4 text-xs">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Provider *</p>
                      <select
                        value={llmProvider}
                        onChange={e => setLlmProvider(e.target.value)}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 font-semibold"
                      >
                        <option value="NVIDIA">NVIDIA</option>
                        <option value="DeepSeek">DeepSeek</option>
                        <option value="OpenAI">OpenAI</option>
                        <option value="OpenRouter">OpenRouter</option>
                        <option value="Google Gemini">Google Gemini</option>
                        <option value="Anthropic">Anthropic</option>
                      </select>
                    </div>

                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">API Key *</p>
                      <input
                        type="password"
                        value={llmApiKey}
                        onChange={e => setLlmApiKey(e.target.value)}
                        placeholder="Enter Provider API Key"
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 font-mono"
                      />
                    </div>

                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Model ID * (Required)</p>
                      <input
                        type="text"
                        value={llmModelId}
                        onChange={e => setLlmModelId(e.target.value)}
                        placeholder={
                          llmProvider === 'OpenAI' ? 'e.g. gpt-4o or o3-mini' :
                          llmProvider === 'DeepSeek' ? 'e.g. deepseek-chat or deepseek-reasoner' :
                          llmProvider === 'NVIDIA' ? 'e.g. meta/llama-3.1-405b-instruct' :
                          llmProvider === 'OpenRouter' ? 'e.g. google/gemini-2.5-pro' :
                          llmProvider === 'Anthropic' ? 'e.g. claude-3-5-sonnet-latest' :
                          'e.g. gemini-2.5-pro'
                        }
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 font-mono"
                      />
                      <p className="text-[10px] text-slate-400 mt-1 leading-normal">
                        Enter the exact supported model ID string. Connection endpoints, API versions, and headers are managed automatically.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 text-xs">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Reasoning Effort</p>
                      <div className="flex gap-4 pt-1">
                        {['low', 'medium', 'high'].map(val => (
                          <label key={val} className="flex items-center gap-2 font-semibold text-slate-700 capitalize cursor-pointer">
                            <input
                              type="radio"
                              name="reasoningEffort"
                              checked={reasoningEffort === val}
                              onChange={() => setReasoningEffort(val)}
                              className="text-blue-600 focus:ring-brand-primary"
                            />
                            <span>{val}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Controls the depth of reasoning for supported reasoning models (like OpenAI o1/o3-mini).</p>
                    </div>

                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Temperature</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="2"
                          step="0.1"
                          value={temperature || '1'}
                          onChange={e => setTemperature(e.target.value)}
                          className="flex-1 accent-blue-600 h-1.5 bg-slate-200 rounded-lg cursor-pointer"
                        />
                        <input
                          type="number"
                          min="0"
                          max="2"
                          step="0.1"
                          value={temperature}
                          onChange={e => setTemperature(e.target.value)}
                          className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-center font-semibold outline-none text-xs"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Controls randomness. Lower values make results more deterministic, higher values make them more creative.</p>
                    </div>

                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Top P</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={topP || '1'}
                          onChange={e => setTopP(e.target.value)}
                          className="flex-1 accent-blue-600 h-1.5 bg-slate-200 rounded-lg cursor-pointer"
                        />
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.05"
                          value={topP}
                          onChange={e => setTopP(e.target.value)}
                          className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-center font-semibold outline-none text-xs"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Nucleus sampling. Probability threshold for considered tokens.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Frequency Penalty</p>
                        <input
                          type="number"
                          min="-2"
                          max="2"
                          step="0.1"
                          value={frequencyPenalty}
                          onChange={e => setFrequencyPenalty(e.target.value)}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Reduces repetitions based on text frequency.</p>
                      </div>

                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Presence Penalty</p>
                        <input
                          type="number"
                          min="-2"
                          max="2"
                          step="0.1"
                          value={presencePenalty}
                          onChange={e => setPresencePenalty(e.target.value)}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Encourages introducing new topics.</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Max Tokens</p>
                      <input
                        type="number"
                        min="1"
                        max="32768"
                        value={maxTokens}
                        onChange={e => setMaxTokens(e.target.value)}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 font-mono"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Specify the maximum output token limit.</p>
                    </div>

                    <div className="pt-2 border-t border-slate-100 flex justify-end">
                      <button
                        type="button"
                        onClick={resetLlmToDefaults}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-[11px] font-bold text-slate-600 cursor-pointer transition-all"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span>Reset to Defaults</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Save and Test section */}
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={saveAndTestLlm}
                      disabled={testingLlm || !llmProvider || !llmApiKey || !llmModelId}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50 transition-all shadow-md shadow-blue-500/10"
                    >
                      {testingLlm ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                      <span>{testingLlm ? 'Testing Connection & Saving...' : 'Save & Test Configuration'}</span>
                    </button>
                  </div>

                  {testResult && (
                    <div className={`p-4 rounded-xl border flex gap-3 text-xs leading-normal animate-fade-in ${testResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                      {testResult.success ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-500 shrink-0" />}
                      <div>
                        <p className="font-bold">{testResult.success ? 'Success' : 'Configuration Error'}</p>
                        <p className="mt-0.5">{testResult.message}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            ) : tab === 'video' ? (
              <Section title="Video & Meetings Configuration" subtitle="Configure video conference providers and workspace-wide meeting parameters. Both platforms can be enabled simultaneously.">
                <div className="space-y-4">
                  {/* Enabled Platforms Section */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1.5">Enabled Video Platforms</label>
                    <p className="text-xs text-slate-500 mb-3">Enable one or both video conferencing platforms for event organizers in this workspace.</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                        <div>
                          <span className="text-xs font-bold text-slate-800 block">Jitsi Meet</span>
                          <span className="text-[11px] text-slate-500">Free open-source video calls generated on demand.</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={jitsiEnabled}
                          onChange={(e) => setJitsiEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand cursor-pointer"
                        />
                      </div>

                      <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                        <div>
                          <span className="text-xs font-bold text-slate-800 block">Daily.co Integration</span>
                          <span className="text-[11px] text-slate-500">Professional REST API video calls with cloud recording, lobby controls, and private tokens.</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={dailyEnabled}
                          onChange={(e) => setDailyEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand cursor-pointer"
                        />
                      </div>

                        <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                          <div>
                            <p className="text-xs font-bold text-slate-800">Notify Admin when booked</p>
                            <p className="text-[11px] text-slate-500">Send immediate email notifications to designated screening staff & workspace admins when booked.</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifyAdminOnBooking}
                            onChange={e => setNotifyAdminOnBooking(e.target.checked)}
                            className="h-5 w-5 rounded border-slate-300 text-brand focus:ring-brand cursor-pointer"
                          />
                      </div>
                    </div>
                  </div>

                  <hr className="border-slate-100" />

                  {/* Default Platform Selector */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1.5">Default Video Provider</label>
                    <p className="text-xs text-slate-500 mb-3">Select the default video platform pre-selected for newly created virtual events.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDefaultVideoProvider('jitsi')}
                        className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${defaultVideoProvider === 'jitsi' ? 'border-brand bg-brand/5 ring-2 ring-brand/20' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-slate-800">Jitsi Meet</span>
                          {defaultVideoProvider === 'jitsi' && <CheckCircle2 className="h-4 w-4 text-brand" />}
                        </div>
                        <p className="text-[11px] text-slate-500">Default for virtual meetings.</p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setDefaultVideoProvider('daily')}
                        className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${defaultVideoProvider === 'daily' ? 'border-brand bg-brand/5 ring-2 ring-brand/20' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-slate-800">Daily.co</span>
                          {defaultVideoProvider === 'daily' && <CheckCircle2 className="h-4 w-4 text-brand" />}
                        </div>
                        <p className="text-[11px] text-slate-500">Default for virtual meetings.</p>
                      </button>
                    </div>
                  </div>

                  {dailyEnabled && (
                    <div className="space-y-4 pt-2 animate-fade-in">
                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Daily.co API Key</label>
                        <p className="text-[11px] text-slate-500 mb-2">Used server-side to create rooms and issue meeting tokens. Stored securely and never exposed to clients.</p>
                        <input
                          type="password"
                          value={dailyApiKey}
                          onChange={(e) => setDailyApiKey(e.target.value)}
                          placeholder="Enter Daily.co API key"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-brand/20 outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Daily Domain / Subdomain (Optional)</label>
                        <input
                          type="text"
                          value={dailyDomain}
                          onChange={(e) => setDailyDomain(e.target.value)}
                          placeholder="e.g. myorg.daily.co"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-brand/20 outline-none"
                        />
                      </div>

                      <hr className="border-slate-100" />

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1.5">Meeting Privacy & Access</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer ${dailyDefaultPrivacy === 'private' ? 'border-brand bg-brand/5' : 'border-slate-200 bg-white'}`}>
                            <input
                              type="radio"
                              name="privacy"
                              checked={dailyDefaultPrivacy === 'private'}
                              onChange={() => setDailyDefaultPrivacy('private')}
                              className="mt-0.5"
                            />
                            <div>
                              <span className="text-xs font-bold text-slate-800 block">Private (Token Required)</span>
                              <span className="text-[11px] text-slate-500">Requires server-generated token. Recommended for privacy and access control.</span>
                            </div>
                          </label>

                          <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer ${dailyDefaultPrivacy === 'public' ? 'border-brand bg-brand/5' : 'border-slate-200 bg-white'}`}>
                            <input
                              type="radio"
                              name="privacy"
                              checked={dailyDefaultPrivacy === 'public'}
                              onChange={() => setDailyDefaultPrivacy('public')}
                              className="mt-0.5"
                            />
                            <div>
                              <span className="text-xs font-bold text-slate-800 block">Public</span>
                              <span className="text-[11px] text-slate-500">Anyone with the room URL can enter without a token.</span>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1.5">Participant Defaults</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={dailyStartAudioOff}
                              onChange={(e) => setDailyStartAudioOff(e.target.checked)}
                              className="rounded border-slate-300 text-brand"
                            />
                            Start participants muted
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={dailyStartVideoOff}
                              onChange={(e) => setDailyStartVideoOff(e.target.checked)}
                              className="rounded border-slate-300 text-brand"
                            />
                            Start participants with camera off
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1.5">In-Meeting Features</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={dailyEnableChat}
                              onChange={(e) => setDailyEnableChat(e.target.checked)}
                              className="rounded border-slate-300 text-brand"
                            />
                            Enable in-meeting text chat
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={dailyEnableScreenShare}
                              onChange={(e) => setDailyEnableScreenShare(e.target.checked)}
                              className="rounded border-slate-300 text-brand"
                            />
                            Enable screen sharing
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1.5">Recording Behavior</label>
                        <select
                          value={dailyEnableRecording}
                          onChange={(e) => setDailyEnableRecording(e.target.value as any)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs outline-none bg-white"
                        >
                          <option value="off">Recording Disabled</option>
                          <option value="cloud">Cloud Recording Enabled</option>
                          <option value="local">Local Recording Enabled</option>
                        </select>
                        <p className="text-[11px] text-slate-400 mt-1">Note: Rooms with recording enabled are preserved until recording processing completes before being cleaned up.</p>
                      </div>
                    </div>
                  )}

                  {dailyError && (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs">
                      {dailyError}
                    </div>
                  )}

                  <div className="pt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={saveDailySettings}
                      disabled={savingDaily}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                    >
                      {savingDaily ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      {savingDaily ? 'Saving...' : 'Save Video Settings'}
                    </button>
                  </div>
                  {dailySaveSuccess && <p className="text-xs font-bold text-emerald-600 text-right">Video settings saved successfully! ✓</p>}
                </div>
              </Section>
            ) : tab === 'legal' ? (
              <Section title="Legal" subtitle="Policies, terms, consent texts and legal documents for this workspace. Everything here is workspace-scoped and drives the public legal pages, consent records, payment screens and the email footer.">
                <LegalSettingsSection workspaceId={ws?.workspaceId || ''} />
              </Section>
            ) : null}
          </div>
        </div>
      </div>
      <DoubleConfirmationModal
        isOpen={doubleConfirmOpen}
        onClose={() => setDoubleConfirmOpen(false)}
        onSuccess={load}
        targetUid={targetUidForChange}
        newRole={newRoleForChange}
        entityType="workspace"
      />

    </div>,
    document.body
  );
}

/* ── Shared building blocks ─────────────────────────────────────────── */

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="max-w-3xl space-y-3">
      <div>
        <p className="text-sm font-black text-slate-900">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, disabled, helpText }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean; helpText?: string }) {
  return (
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <input
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
      />
      {helpText && <p className="text-[10px] text-amber-600 font-semibold mt-1">{helpText}</p>}
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value || '') ? value! : '#2563eb'} onChange={e => onChange(e.target.value)} className="h-9 w-10 rounded-lg border border-slate-200 cursor-pointer" />
        <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#2563eb" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/20" />
      </div>
    </div>
  );
}

function BrandingUploader({ label, size, url, uploading, onPick, onRemove }: {
  label: string; size: string; url?: string; uploading: boolean; onPick: (f: File) => void; onRemove: () => void;
}) {
  return (
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5">{label}</p>
      <div className="flex items-center gap-3">
        <div className={`${size} rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0`}>
          {url ? <img src={url} alt={label} className="h-full w-full object-contain" /> : <ImageIcon className="h-5 w-5 text-slate-300" />}
        </div>
        <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }} />
        </label>
        {url && <button onClick={onRemove} className="text-xs font-bold text-rose-500 hover:underline cursor-pointer" aria-label={`Remove logo for ${label}`}>Remove</button>}
      </div>
    </div>
  );
}

/** None / View / Edit segmented toggle used by the custom-role builder. */
function PermToggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const options = [
    { key: 'none', label: 'None', activeCls: 'text-slate-600' },
    { key: 'view', label: 'View', activeCls: 'text-blue-600' },
    { key: 'edit', label: 'Edit', activeCls: 'text-emerald-600' },
  ];
  return (
    <div className="inline-flex items-center rounded-lg bg-slate-100 p-0.5 gap-0.5 shrink-0">
      {options.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${value === o.key ? `bg-white shadow-sm ${o.activeCls}` : 'text-slate-400 hover:text-slate-600'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Applicants visibility scope: ALL applicants or only the ones assigned to the member. */
function ApplicantScopeToggle({ value, onChange }: { value: 'all' | 'assigned'; onChange: (v: 'all' | 'assigned') => void }) {
  return (
    <div className="inline-flex items-center rounded-lg bg-white border border-slate-200 p-0.5 gap-0.5 shrink-0">
      <button
        onClick={() => onChange('all')}
        className={`px-2 py-0.5 rounded-md text-[11px] font-bold transition-all cursor-pointer ${value === 'all' ? 'bg-brand-primary text-white' : 'text-slate-400 hover:text-slate-600'}`}
        title="See every applicant in the workspace"
      >
        All
      </button>
      <button
        onClick={() => onChange('assigned')}
        className={`px-2 py-0.5 rounded-md text-[11px] font-bold transition-all cursor-pointer ${value === 'assigned' ? 'bg-brand-primary text-white' : 'text-slate-400 hover:text-slate-600'}`}
        title="Only see applicants assigned to this member"
      >
        Assigned Only
      </button>
    </div>
  );
}

function DomainStatusBadge({ status }: { status: string }) {
  const verified = status === 'verified' || status === 'active';
  if (verified) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md"><CheckCircle2 className="h-3 w-3" /> Verified</span>;
  }
  if (status === 'pending' || status === 'processing') {
    return <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md"><Clock className="h-3 w-3" /> Pending</span>;
  }
  if (status === 'lost') {
    return <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md"><AlertTriangle className="h-3 w-3" /> Verification Lost</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md"><XCircle className="h-3 w-3" /> Unverified</span>;
}

/** A DNS record row with one-click copy. */
function DnsRecordRow({ type, host, value }: { type: string; host: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">{type}</span>
        <button onClick={() => copy(value)} className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-brand cursor-pointer">
          {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-slate-400 w-10 shrink-0">Host</span>
          <code className="text-[11px] font-mono text-slate-700 break-all">{host}</code>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-[10px] font-black text-slate-400 w-10 shrink-0 mt-0.5">Value</span>
          <code className="text-[11px] font-mono text-slate-700 break-all leading-snug">{value}</code>
        </div>
      </div>
    </div>
  );
}
